import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';

// Typing state is EPHEMERAL: it rides Supabase Realtime *broadcast*, so a
// keystroke never touches the database. Each keypress refreshes a short TTL;
// if someone closes the tab mid-sentence their indicator expires on its own.

const TYPING_TTL_MS = 4000;   // how long a peer stays "typing" without a refresh
const SEND_THROTTLE_MS = 1500; // don't broadcast on literally every keystroke

export interface TypingPeer {
  id: string;
  name: string;
}

export function useTyping(conversationId: string | null, selfId: string | undefined, selfName: string) {
  const [peers, setPeers] = useState<TypingPeer[]>([]);
  const channelRef = useRef<any>(null);
  const lastSentRef = useRef(0);
  // id -> timeout that clears that peer when their TTL lapses
  const expiryRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    // Reset any carry-over state when switching threads.
    setPeers([]);
    expiryRef.current.forEach((t) => clearTimeout(t));
    expiryRef.current.clear();

    if (!conversationId || !selfId) {
      channelRef.current = null;
      return;
    }

    const channel = supabase.channel(`chat-typing-${conversationId}`, {
      config: { broadcast: { self: false } },
    });

    channel
      .on('broadcast', { event: 'typing' }, ({ payload }: any) => {
        const { id, name, typing } = payload || {};
        if (!id || id === selfId) return;

        const existing = expiryRef.current.get(id);
        if (existing) clearTimeout(existing);

        if (!typing) {
          expiryRef.current.delete(id);
          setPeers((prev) => prev.filter((p) => p.id !== id));
          return;
        }

        setPeers((prev) => (prev.some((p) => p.id === id) ? prev : [...prev, { id, name: name || 'Someone' }]));
        expiryRef.current.set(
          id,
          setTimeout(() => {
            expiryRef.current.delete(id);
            setPeers((prev) => prev.filter((p) => p.id !== id));
          }, TYPING_TTL_MS),
        );
      })
      .subscribe();

    channelRef.current = channel;

    return () => {
      expiryRef.current.forEach((t) => clearTimeout(t));
      expiryRef.current.clear();
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [conversationId, selfId]);

  /** Call on each keystroke — throttled internally. */
  const notifyTyping = useCallback(() => {
    if (!channelRef.current || !selfId) return;
    const now = Date.now();
    if (now - lastSentRef.current < SEND_THROTTLE_MS) return;
    lastSentRef.current = now;
    channelRef.current.send({
      type: 'broadcast',
      event: 'typing',
      payload: { id: selfId, name: selfName, typing: true },
    });
  }, [selfId, selfName]);

  /** Call after sending / clearing the box so peers stop seeing the indicator. */
  const notifyStopped = useCallback(() => {
    if (!channelRef.current || !selfId) return;
    lastSentRef.current = 0;
    channelRef.current.send({
      type: 'broadcast',
      event: 'typing',
      payload: { id: selfId, name: selfName, typing: false },
    });
  }, [selfId, selfName]);

  return { typingPeers: peers, notifyTyping, notifyStopped };
}

/** "Ana is typing…" · "Ana and Ben are typing…" · "3 people are typing…" */
export function typingLabel(peers: TypingPeer[]): string {
  if (peers.length === 0) return '';
  if (peers.length === 1) return `${peers[0].name} is typing`;
  if (peers.length === 2) return `${peers[0].name} and ${peers[1].name} are typing`;
  return `${peers.length} people are typing`;
}
